/**
 * Supabase Storage (`foreducator-media`) 객체 삭제 헬퍼.
 *
 * 업로드·삭제 매트릭스(담당 lib):
 * - 문서 서명: supabaseDocumentSign.ts (PDF + document-sign-signatures/)
 * - 가정통신문: supabaseConsentForm.ts (PDF + consent-form-signatures/)
 * - 등록부: supabaseRegistry.ts (registry-sign/)
 * - 커뮤니티: supabaseCommunity.ts (tboard_post / tboard_postfile)
 * - 클래스메이트 게임: supabaseGames.ts (question_images / answer_images)
 * - 분실물: supabaseLostFound.ts (lost-found/…; 비밀번호 삭제는 delete_lost_item_by_password RPC)
 * - 프로필: supabaseProfile.ts (base64url 키 — URL에서 키 추출 후 삭제)
 * - Informate: supabaseInformate.ts / api/informate/books/[bookId]/delete.js (원본 상대 경로 → base64url 키)
 * - Informate vibe-sites: supabaseInformateVibeSites.ts (preview_image_url + description rich-text imgs)
 * - AI 파일 수합: pages/workmate/file-inbox/lib (file-inbox-uploads; 캠페인·제출 삭제·교체 시 prefix/path 정리)
 * - 홈 히어로 광고: api/admin/home-hero-ad.js (home-hero-ad/; 교체·삭제 시 이전 객체 삭제)
 *
 * 규칙: 레코드 삭제 시 관련 Storage도 반드시 함께 삭제 (.cursor/rules/storage-cleanup-on-delete.mdc).
 *
 * Staging 수동 검증: 각 기능에서 파일 업로드 → 삭제(또는 교체) 후 Supabase Dashboard
 * Storage → 해당 버킷에서 객체가 사라졌는지 확인.
 */
import { supabase } from "./supabase";
import { fixSupabasePublicStorageUrl } from "./supabaseUrl";

export const FOREducator_MEDIA_BUCKET = "foreducator-media";

const REMOVE_BATCH = 1000;

/** Signature storage folder prefixes under foreducator-media */
export const REGISTRY_SIGN_STORAGE_PREFIX = "registry-sign";
export const CONSENT_FORM_SIG_STORAGE_PREFIX = "consent-form-signatures";
export const DOCUMENT_SIGN_SIG_STORAGE_PREFIX = "document-sign-signatures";

/** Django Informate / 프로필과 동일: 상대 경로 → Storage object name */
export function informatePathToStorageKey(relativePath: string): string {
  const bytes = new TextEncoder().encode(relativePath);
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * DB에 저장된 값(상대 경로) 또는 public URL에서 `storage.remove`용 객체 키를 얻습니다.
 * 프로필·Informate처럼 URL 경로가 base64url인 경우도 그대로 반환합니다.
 */
export function foreducatorMediaObjectKey(raw: string | null | undefined): string | null {
  if (!raw || typeof raw !== "string") return null;
  const s = raw.trim();
  if (!s) return null;

  if (s.startsWith("http://") || s.startsWith("https://")) {
    const fixed = fixSupabasePublicStorageUrl(s);
    const marker = "/object/public/foreducator-media/";
    const idx = fixed.indexOf(marker);
    if (idx === -1) return null;
    const rest = fixed.slice(idx + marker.length).split("?")[0];
    try {
      return decodeURIComponent(rest);
    } catch {
      return rest;
    }
  }

  let p = s.replace(/^\/+/, "");
  if (p.startsWith("media/")) p = p.slice("media/".length);
  return p || null;
}

/** Informate `books_book.image` 등 원본 상대 경로 → 버킷 키로 삭제 */
export async function removeInformateMediaByOriginalPath(originalPath: string | null | undefined): Promise<void> {
  if (!supabase || !originalPath?.trim()) return;
  const clean = originalPath.replace(/^\/media\//, "").replace(/^\//, "");
  if (!clean) return;
  const key = informatePathToStorageKey(clean);
  const { error } = await supabase.storage.from(FOREducator_MEDIA_BUCKET).remove([key]);
  if (error) throw error;
}

function isStorageFileEntry(entry: { id?: string | null; metadata?: Record<string, unknown> | null }): boolean {
  if (entry.id) return true;
  const size = entry.metadata && typeof entry.metadata.size === "number" ? entry.metadata.size : null;
  return size != null && size >= 0;
}

async function listStoragePrefixRecursive(bucket: string, folder: string): Promise<string[]> {
  if (!supabase) throw new Error("Supabase client not initialized");
  const files: string[] = [];
  const { data, error } = await supabase.storage.from(bucket).list(folder, { limit: 1000 });
  if (error || !data?.length) return files;
  for (const item of data) {
    const child = `${folder}/${item.name}`;
    if (isStorageFileEntry(item)) {
      files.push(child);
    } else {
      files.push(...(await listStoragePrefixRecursive(bucket, child)));
    }
  }
  return files;
}

/** 지정 버킷에서 prefix 아래 모든 객체 경로를 재귀 조회합니다. */
export async function listStoragePrefix(bucket: string, prefix: string): Promise<string[]> {
  const root = prefix.replace(/^\/+|\/+$/g, "");
  if (!root) return [];
  try {
    return await listStoragePrefixRecursive(bucket, root);
  } catch {
    return [];
  }
}

/** `foreducator-media` 버킷에서 prefix 아래 모든 객체 경로를 재귀 조회합니다. */
export async function listForeducatorMediaPrefix(prefix: string): Promise<string[]> {
  return listStoragePrefix(FOREducator_MEDIA_BUCKET, prefix);
}

/** public URL 또는 상대 경로에서 지정 버킷의 object key를 추출합니다. */
export function storageObjectKeyFromUrl(
  bucket: string,
  raw: string | null | undefined
): string | null {
  if (!raw || typeof raw !== "string") return null;
  const s = raw.trim();
  if (!s) return null;
  if (bucket === FOREducator_MEDIA_BUCKET) {
    return foreducatorMediaObjectKey(s);
  }
  if (s.startsWith("http://") || s.startsWith("https://")) {
    const fixed = fixSupabasePublicStorageUrl(s);
    const marker = `/object/public/${bucket}/`;
    const idx = fixed.indexOf(marker);
    if (idx === -1) return null;
    const rest = fixed.slice(idx + marker.length).split("?")[0];
    try {
      return decodeURIComponent(rest);
    } catch {
      return rest;
    }
  }
  return s.replace(/^\/+/, "") || null;
}

/** 지정 버킷에서 여러 경로/URL을 일괄 삭제합니다. */
export async function removeStorageObjects(
  bucket: string,
  paths: (string | null | undefined)[]
): Promise<void> {
  if (!supabase) throw new Error("Supabase client not initialized");
  const keys = [
    ...new Set(paths.map((p) => storageObjectKeyFromUrl(bucket, p)).filter(Boolean)),
  ] as string[];
  if (keys.length === 0) return;
  for (let i = 0; i < keys.length; i += REMOVE_BATCH) {
    const chunk = keys.slice(i, i + REMOVE_BATCH);
    const { error } = await supabase.storage.from(bucket).remove(chunk);
    if (error) throw error;
  }
}

/**
 * DB URL과 Storage prefix listing 결과를 병합해 삭제합니다.
 * listing 실패 시 dbUrls 기반 삭제만 시도합니다.
 */
export async function removeStorageByPrefixAndUrls(
  bucket: string,
  prefix: string,
  dbUrls: (string | null | undefined)[]
): Promise<void> {
  if (!supabase) throw new Error("Supabase client not initialized");
  const fromDb = dbUrls
    .map((u) => storageObjectKeyFromUrl(bucket, u))
    .filter(Boolean) as string[];
  let fromStorage: string[] = [];
  try {
    fromStorage = await listStoragePrefix(bucket, prefix);
  } catch {
    fromStorage = [];
  }
  const normalizedPrefix = prefix.replace(/^\/+|\/+$/g, "");
  const allKeys = [
    ...new Set([
      ...fromDb,
      ...fromStorage.filter((p) => p === normalizedPrefix || p.startsWith(`${normalizedPrefix}/`)),
    ]),
  ];
  if (allKeys.length === 0) return;
  for (let i = 0; i < allKeys.length; i += REMOVE_BATCH) {
    const chunk = allKeys.slice(i, i + REMOVE_BATCH);
    const { error } = await supabase.storage.from(bucket).remove(chunk);
    if (error) throw error;
  }
}

export const COMMUNITY_RICH_TEXT_EDITOR_PREFIX = "community/rich-text-editor";

/** HTML 본문에서 foreducator-media community/rich-text-editor Storage 키를 추출합니다. */
export function extractForeducatorMediaKeysFromHtml(html: string | null | undefined): string[] {
  if (!html || typeof html !== "string") return [];
  const keys = new Set<string>();
  const srcPattern = /\bsrc\s*=\s*["']([^"']+)["']/gi;
  let match: RegExpExecArray | null;
  while ((match = srcPattern.exec(html)) !== null) {
    const key = foreducatorMediaObjectKey(match[1]);
    if (key && key.startsWith(`${COMMUNITY_RICH_TEXT_EDITOR_PREFIX}/`)) {
      keys.add(key);
    }
  }
  return [...keys];
}

/** Storage URL·상대 경로 배열에서 서명 객체 키를 추출합니다. Base64 data URL은 스킵합니다. */
export function collectSignatureStorageKeysFromUrls(urls: (string | null | undefined)[]): string[] {
  const keys = new Set<string>();
  for (const raw of urls) {
    if (!raw || typeof raw !== "string") continue;
    const s = raw.trim();
    if (!s || s.startsWith("data:")) continue;
    const key = foreducatorMediaObjectKey(s);
    if (key) keys.add(key);
  }
  return [...keys];
}

/** 가정통신문 field_values JSONB에서 consent-form-signatures Storage 키를 추출합니다. */
export function collectSignatureStorageKeysFromConsentFieldValues(
  fieldValues: Record<string, string> | null | undefined
): string[] {
  if (!fieldValues) return [];
  return collectSignatureStorageKeysFromUrls(Object.values(fieldValues)).filter((k) =>
    k.startsWith(`${CONSENT_FORM_SIG_STORAGE_PREFIX}/`)
  );
}

/**
 * DB에 저장된 URL과 Storage prefix listing 결과를 병합해 삭제합니다.
 * listing 실패 시 dbUrls 기반 삭제만 시도합니다.
 */
export async function removeForeducatorMediaByPrefixAndUrls(
  prefix: string,
  dbUrls: (string | null | undefined)[]
): Promise<void> {
  await removeStorageByPrefixAndUrls(FOREducator_MEDIA_BUCKET, prefix, dbUrls);
}

/**
 * 여러 DB/URL 값에 대해 중복 제거 후 한 번에 삭제합니다.
 */
export async function removeForeducatorMediaObjects(
  paths: (string | null | undefined)[]
): Promise<void> {
  await removeStorageObjects(FOREducator_MEDIA_BUCKET, paths);
}
